import { ErrorBoundary } from './ErrorBoundary'
import { GuessGrid } from './GuessGrid'
import { Keyboard } from './Keyboard'
import { Alert } from './Alert'
import { useWordle } from '../hooks/useWordle'
import { useGameSettings } from '../contexts/GameSettingsContext'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { GAME_MODES } from '../constants/gameConfig'
import { trackLoadTimeExceeded } from '../services/gameAnalytics'

// No props needed anymore
export const GameBoard = () => {
  const navigate = useNavigate()
  const { settings } = useGameSettings()
  const {
    guesses,
    gameOver,
    won,
    secondsTaken,
    stats,
    alerts,
    keyStates,
    addLetter,
    removeLetter,
    submitGuess,
    targetWord,
    definition,
    setterNote,
  } = useWordle({
    mode: settings.selectedMode,
  })

  // Get the current game mode configuration
  const currentGameMode = GAME_MODES[settings.selectedMode]

  // Track page load time performance
  useEffect(() => {
    // Get the time since navigation start
    const loadTime = performance.now()
    const LOAD_TIME_THRESHOLD = 1000 // 1 second threshold

    if (loadTime > LOAD_TIME_THRESHOLD) {
      try {
        trackLoadTimeExceeded(
          loadTime,
          window.location.pathname,
          LOAD_TIME_THRESHOLD
        )
      } catch (error) {
        console.error('Failed to track load time:', error)
      }
    }
  }, [])

  // When game is over, navigate to completion page
  useEffect(() => {
    if (gameOver) {
      // Process guesses for navigation - include tile state information not just letters
      const processedGuesses = guesses.map((row) =>
        row.map((tile) => ({
          letter: tile.letter,
          state: tile.state,
        }))
      )

      // Navigate to completion page with game results
      navigate('/zword/complete', {
        state: {
          won,
          secondsTaken,
          guesses: processedGuesses,
          stats,
          targetWord,
          definition,
          setterNote,
        },
      })
    }
  }, [gameOver, guesses, navigate, stats, won, secondsTaken, targetWord, definition, setterNote])

  return (
    <ErrorBoundary
      componentName="GameBoard"
      metadata={{
        userId: 'user123',
        additionalData: {
          gameMode: 'daily',
          attempts: 3,
        },
      }}
      onError={(error) => {
        console.error('Game error:', error)
      }}
    >
      <div className="flex flex-col items-center gap-4">
        <Alert alerts={alerts} />

        <div className="grid-container">
          <GuessGrid guesses={guesses} />
        </div>

        <Keyboard
          onKey={(key) => {
            if (!gameOver) {
              if (key === 'ENTER') submitGuess()
              else if (key === 'DELETE') removeLetter()
              else addLetter(key)
            }
          }}
          keyStates={keyStates}
          language={currentGameMode.language}
          wordLength={settings.selectedMode}
        />
      </div>
    </ErrorBoundary>
  )
}
